// The open ticket's follow-up, from the conversation header: set one, or see, change or cancel the live one.

import { useId, useMemo, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { atomWithQuery, queryClientAtom } from 'jotai-tanstack-query';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';
import { cn, getApiErrorMessage } from '@/lib/utils';
import {
  formatWaitDuration,
  fromWaitProgram,
  splitWaitMinutes,
  typedWaitMinutes,
  type WaitProgram,
  type WaitUnit,
} from '@/modules/protocols/utils/wait-program';
import { followupsService } from '@/services/followups.service';
import { followupHeaderLabel, followupStateText } from './followup-copy';
import { followupActionError } from './followup-format';
import { When } from './followup-row';
import { openFollowupIdAtom } from './followup-state';
import { HeaderIconButton } from '../header-icon-button';
import { FollowupIcon } from '../icons';
import type { TicketFollowupInput } from './types';

const UNITS: { value: WaitUnit; label: string }[] = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
];

const PRESETS: { minutes: number; label: string }[] = [
  { minutes: 4 * 60, label: '4 hours' },
  { minutes: 1440, label: '1 day' },
  { minutes: 2 * 1440, label: '2 days' },
  { minutes: 7 * 1440, label: '1 week' },
];
const CUSTOM = 'custom';

const fieldClass =
  'h-8 rounded-xl border border-gray-500 bg-white px-2 text-[13px] text-text-body focus-visible:outline-2 focus-visible:outline-ring aria-invalid:border-red-500';

const chipClass = cn(
  'h-7 rounded-full border border-gray-500 bg-white px-2.5 text-[12px] text-text-body transition-colors hover:bg-gray-100',
  'focus-visible:outline-2 focus-visible:outline-ring',
  'data-[state=checked]:border-text-heading data-[state=checked]:bg-text-heading data-[state=checked]:text-white',
);

const cardClass = cn(
  'flex w-full items-start gap-2.5 rounded-xl border border-gray-500 bg-white px-3 py-2 text-left transition-colors hover:bg-gray-100',
  'focus-visible:outline-2 focus-visible:outline-ring data-[state=checked]:border-text-heading data-[state=checked]:bg-gray-100',
);

/** What the form starts from: a new follow-up's defaults, or the one it is replacing. */
export interface TicketFollowupDraft {
  minutes: number;
  then: TicketFollowupInput['then'];
  instruction: string;
  note: string;
}

const NEW_FOLLOWUP: TicketFollowupDraft = { minutes: 2 * 1440, then: 'nudge', instruction: '', note: '' };

/** The live follow-up's wait, nudge text or reminder note, to start a change from. */
function draftFromProgram(program: WaitProgram): TicketFollowupDraft {
  const { minutes, nudge, prompt } = fromWaitProgram(program);
  const start = program.steps.find((step) => step.id === program.start) ?? program.steps[0];
  const note = start?.otherwise.do === 'handoff' ? (start.otherwise.note ?? '') : '';
  return nudge ? { minutes, then: 'nudge', instruction: prompt, note: '' } : { minutes, then: 'remind', instruction: '', note };
}

export function TicketFollowupForm({
  ticketId,
  customerName,
  replacing,
  initial = NEW_FOLLOWUP,
  onDone,
  onCancel,
}: {
  ticketId: number;
  customerName: string;
  replacing: boolean;
  initial?: TicketFollowupDraft;
  onDone: () => void;
  onCancel: () => void;
}) {
  const id = useId();
  const initialPreset = PRESETS.some((preset) => preset.minutes === initial.minutes) ? String(initial.minutes) : CUSTOM;
  const [preset, setPreset] = useState(initialPreset);
  const [amount, setAmount] = useState(() => String(splitWaitMinutes(initial.minutes).amount));
  const [unit, setUnit] = useState<WaitUnit>(() => splitWaitMinutes(initial.minutes).unit);
  const [then, setThen] = useState(initial.then);
  const [instruction, setInstruction] = useState(initial.instruction);
  const [note, setNote] = useState(initial.note);
  const [instructionMissing, setInstructionMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const custom = preset === CUSTOM;
  const minutes = custom ? typedWaitMinutes(amount, unit) : Number(preset);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (minutes === null) return;
    if (then === 'nudge' && !instruction.trim()) {
      setInstructionMissing(true);
      return;
    }
    const input: TicketFollowupInput =
      then === 'nudge' ? { minutes, then, instruction: instruction.trim() } : { minutes, then, ...(note.trim() ? { note: note.trim() } : {}) };
    setBusy(true);
    setError(null);
    try {
      await followupsService.setForTicket(ticketId, input);
      toast.success(`Follow-up set for ${formatWaitDuration(minutes)}`);
      onDone();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not set the follow-up.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={(event) => void submit(event)}
      aria-labelledby={`${id}-title`}
      className="flex flex-col gap-4 text-[13px] leading-[18px] text-text-body"
      noValidate
    >
      <div className="flex flex-col gap-2">
        <h2 id={`${id}-title`} className="text-[14px] font-semibold leading-5 text-text-heading">
          Follow up if {customerName} doesn&apos;t reply
        </h2>
        {replacing && <p className="rounded-xl bg-yellow-50 px-3 py-2 text-[12px]">This replaces the current follow-up.</p>}
      </div>

      <div className="flex flex-col gap-2">
        <span id={`${id}-after`} className="text-[12px] font-medium text-gray-800">
          After
        </span>
        <RadioGroupPrimitive.Root aria-labelledby={`${id}-after`} value={preset} onValueChange={setPreset} className="flex flex-wrap gap-1.5">
          {PRESETS.map((option) => (
            <RadioGroupPrimitive.Item key={option.minutes} value={String(option.minutes)} className={chipClass}>
              {option.label}
            </RadioGroupPrimitive.Item>
          ))}
          <RadioGroupPrimitive.Item value={CUSTOM} className={chipClass}>
            Custom
          </RadioGroupPrimitive.Item>
        </RadioGroupPrimitive.Root>
        {custom && (
          <>
            <div className="flex gap-2">
              <input
                id={`${id}-amount`}
                type="number"
                inputMode="numeric"
                min={1}
                step={1}
                aria-label="Amount"
                aria-invalid={minutes === null}
                aria-describedby={minutes === null ? `${id}-amount-error` : undefined}
                className={`${fieldClass} w-20`}
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
              <select aria-label="Unit" className={`${fieldClass} flex-1`} value={unit} onChange={(event) => setUnit(event.target.value as WaitUnit)}>
                {UNITS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            {minutes === null && (
              <p id={`${id}-amount-error`} className="text-[12px] text-red-600">
                Between 5 minutes and 30 days.
              </p>
            )}
          </>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <span id={`${id}-then`} className="text-[12px] font-medium text-gray-800">
          Then
        </span>
        <RadioGroupPrimitive.Root
          aria-labelledby={`${id}-then`}
          value={then}
          onValueChange={(value) => setThen(value as TicketFollowupInput['then'])}
          className="flex flex-col gap-1.5"
        >
          {(
            [
              ['nudge', 'Kim drafts a nudge', "You review it before it's sent."],
              ['remind', 'Remind me', 'It comes back to you in Needs a person.'],
            ] as const
          ).map(([value, title, sub]) => (
            <RadioGroupPrimitive.Item
              key={value}
              value={value}
              aria-labelledby={`${id}-${value}-title`}
              aria-describedby={`${id}-${value}-sub`}
              className={cardClass}
            >
              <span className="mt-0.5 flex size-3.5 shrink-0 items-center justify-center rounded-full border border-gray-700 bg-white">
                <RadioGroupPrimitive.Indicator className="size-1.5 rounded-full bg-text-heading" />
              </span>
              <span className="flex flex-col gap-0.5">
                <span id={`${id}-${value}-title`} className="font-medium text-text-heading">
                  {title}
                </span>
                <span id={`${id}-${value}-sub`} className="text-[12px] leading-4 text-gray-800">
                  {sub}
                </span>
              </span>
            </RadioGroupPrimitive.Item>
          ))}
        </RadioGroupPrimitive.Root>
      </div>

      {then === 'nudge' ? (
        <div className="flex flex-col gap-1.5">
          <label htmlFor={`${id}-instruction`} className="text-[12px] font-medium text-gray-800">
            Note to Kim AI
          </label>
          <Textarea
            id={`${id}-instruction`}
            required
            maxLength={2000}
            placeholder="e.g. Ask if the parcel arrived. If it's still in transit, offer a free reship."
            aria-invalid={instructionMissing}
            aria-describedby={instructionMissing ? `${id}-instruction-error` : `${id}-instruction-help`}
            value={instruction}
            onChange={(event) => {
              setInstruction(event.target.value);
              if (event.target.value.trim()) setInstructionMissing(false);
            }}
            className="min-h-20 text-[13px] aria-invalid:border-red-500"
          />
          {instructionMissing ? (
            <p id={`${id}-instruction-error`} className="text-[12px] text-red-600">
              Add a note so Kim knows what to ask.
            </p>
          ) : (
            <p id={`${id}-instruction-help`} className="text-[12px] text-gray-700">
              Kim uses this to write the nudge.
            </p>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          <label htmlFor={`${id}-note`} className="text-[12px] font-medium text-gray-800">
            Note for yourself (optional)
          </label>
          <Textarea id={`${id}-note`} maxLength={500} value={note} onChange={(event) => setNote(event.target.value)} className="min-h-16 text-[13px]" />
        </div>
      )}

      {error && (
        <p role="alert" className="text-[12px] text-red-600">
          {error}
        </p>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={busy || minutes === null}>
          {busy ? 'Saving…' : replacing ? 'Update follow-up' : 'Set follow-up'}
        </Button>
      </div>
    </form>
  );
}

/** Change: the form, started from the live follow-up's own settings once its detail loads. */
function ChangeFollowupForm({ followupId, ...props }: Omit<React.ComponentProps<typeof TicketFollowupForm>, 'initial' | 'replacing'> & { followupId: string }) {
  const detailAtom = useMemo(
    () =>
      atomWithQuery((get) => ({
        // The drawer's key, so an open drawer and this form share one fetch.
        queryKey: ['sentinel', 'followups', 'detail', buScopeKey(get), followupId],
        queryFn: () => followupsService.get(followupId, buScopeParams(get)),
        retry: false,
      })),
    [followupId],
  );
  const detail = useAtomValue(detailAtom);
  if (detail.isPending) {
    return (
      <div className="flex flex-col gap-3" aria-busy="true" aria-label="Loading the current follow-up">
        <Skeleton className="h-5 w-3/4 rounded-md" />
        <Skeleton className="h-7 w-full rounded-full" />
        <Skeleton className="h-24 w-full rounded-xl" />
      </div>
    );
  }
  // Never fall back to defaults here: saving would replace the live follow-up with a different one.
  if (!detail.data) {
    return (
      <div role="alert" className="flex flex-col items-start gap-2 text-[13px]">
        <p className="text-red-600">{getApiErrorMessage(detail.error, 'The current follow-up could not be loaded.')}</p>
        <Button variant="outline" size="sm" onClick={() => void detail.refetch()}>
          Try again
        </Button>
      </div>
    );
  }
  return <TicketFollowupForm {...props} replacing initial={draftFromProgram(detail.data.program)} />;
}

/** "Follow up" in the open ticket's header; with a live follow-up, its state and a small menu. */
export function TicketFollowupControl({ ticketId, customerName }: { ticketId: number; customerName: string | null }) {
  const queryAtom = useMemo(
    () =>
      atomWithQuery(() => ({
        // Under the follow-ups prefix, so every follow-up change refreshes it too.
        queryKey: ['sentinel', 'followups', 'ticket', ticketId],
        queryFn: () => followupsService.getForTicket(ticketId),
        retry: false,
      })),
    [ticketId],
  );
  const query = useAtomValue(queryAtom);
  const queryClient = useAtomValue(queryClientAtom);
  const setOpenFollowupId = useSetAtom(openFollowupIdAtom);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'menu' | 'form' | 'confirm'>('form');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const live = query.data && query.data.state !== 'ended' ? query.data : null;
  const firstName = customerName?.trim().split(/\s+/)[0] || 'the customer';
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['sentinel', 'followups'] });
  const done = () => {
    setOpen(false);
    refresh();
  };

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) return;
    setMode(live ? 'menu' : 'form');
    setError(null);
  };

  const cancelFollowup = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      await followupsService.cancel(id);
      toast.success('Follow-up cancelled');
      setOpen(false);
      refresh();
    } catch (err) {
      setError(followupActionError(err, 'Could not cancel the follow-up.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <HeaderIconButton label={live ? followupHeaderLabel(live) : 'Follow up'} disabled={query.isPending}>
          <FollowupIcon />
        </HeaderIconButton>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] rounded-2xl bg-white" aria-label={live && mode !== 'form' ? 'Follow-up' : 'Set a follow-up'}>
        {query.isError ? (
          // Not knowing whether a follow-up exists is not "none": a new one could silently replace it.
          <div role="alert" className="flex flex-col items-start gap-2 text-[13px]">
            <p className="text-red-600">{getApiErrorMessage(query.error, "This ticket's follow-up could not be loaded.")}</p>
            <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
              Try again
            </Button>
          </div>
        ) : query.isPending ? (
          <div aria-busy="true" aria-label="Loading the ticket's follow-up">
            <Skeleton className="h-24 w-full rounded-xl" />
          </div>
        ) : mode === 'form' || !live ? (
          live ? (
            <ChangeFollowupForm followupId={live.id} ticketId={ticketId} customerName={firstName} onDone={done} onCancel={() => setOpen(false)} />
          ) : (
            <TicketFollowupForm ticketId={ticketId} customerName={firstName} replacing={false} onDone={done} onCancel={() => setOpen(false)} />
          )
        ) : (
          <div className="flex flex-col gap-3 text-[13px] leading-[18px] text-text-body">
            <div className="flex flex-col gap-0.5">
              <p className="font-medium">{followupStateText(live)}</p>
              {live.nextDueAt && (
                <p className="text-[12px] text-gray-800">
                  {new Date(live.nextDueAt).getTime() <= Date.now() ? 'Due now' : (
                    <>
                      Next: <When iso={live.nextDueAt} />
                    </>
                  )}
                </p>
              )}
            </div>
            {mode === 'confirm' ? (
              <div role="group" aria-label="Confirm cancel" className="flex flex-wrap items-center gap-2">
                <span>Cancel this follow-up?</span>
                <Button variant="destructive" size="sm" disabled={busy} onClick={() => void cancelFollowup(live.id)}>
                  Yes, cancel
                </Button>
                <Button variant="outline" size="sm" disabled={busy} onClick={() => setMode('menu')}>
                  Keep
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-stretch gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="justify-start"
                  onClick={() => {
                    setOpen(false);
                    setOpenFollowupId(live.id);
                  }}
                >
                  View details
                </Button>
                {/* Once it is sending it is going out: nothing left to change or cancel. */}
                <Button variant="ghost" size="sm" className="justify-start" disabled={live.state === 'sending'} onClick={() => setMode('form')}>
                  Change
                </Button>
                <Button variant="ghost" size="sm" className="justify-start text-red-600 hover:text-red-700" disabled={live.state === 'sending'} onClick={() => setMode('confirm')}>
                  Cancel follow-up
                </Button>
              </div>
            )}
            {error && (
              <p role="alert" className="text-[12px] text-red-600">
                {error}
              </p>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
